/**
 * Minimal shape of an entry returned by the `/admin/locations` endpoint.
 * Centralized here because multiple pro-detail surfaces (LegsGrid leg
 * origin/destination cells, AddlInfoView's reuse-origin TypeAhead) consume
 * the same payload and were each defining their own copy.
 *
 * Mirrors the SELECT columns in `admin.get_locations` — keep them in sync
 * when adding fields. The full response from the endpoint includes
 * additional columns (e.g. coordinates, ladd2) that the current consumers
 * don't read; add them here if a new consumer needs them.
 */
export interface LocationLite {
  recno: number;
  lname: string;
  cyalias: string;
  ladd: string;
  lcity: string;
  lstate: string;
  ladd2?: string;
  lzip?: string;
  locationtype?: string;
}

/** One contact row for a location (cms.contactinfo + contacttype label). */
export interface LocationContact {
  type: string;
  name: string;
  phone: string;
  email: string;
}

/**
 * Full location detail shown in the legs-grid Location info popup. Returned by
 * `GET /admin/locations/details?recnos=…`, keyed by recno (string). A location
 * with `specialinstructions` drives the orange info icon. The address /
 * communication / fax fields are optional because cached or mocked payloads
 * from before the backend added them may lack them.
 */
export interface LocationInfo {
  recno: number;
  lname: string;
  /** Retired in Data Management, but legs still reference it — `/admin/locations/details`
   *  deliberately does not filter these out, so the screen has to say so instead.
   *
   *  ONLY MEANINGFUL WHEN `recno > 0`. recno 0 is the "no location set" sentinel and is
   *  itself flagged deleted, so trusting this blindly paints every empty stop red
   *  (3,712 open legs on prod). Use `isDeletedLocation`. */
  deleted?: boolean;
  rcvhours1: string;
  rcvhours2: string;
  apptrequired: string;
  droplive: string;
  additionalinfo: string;
  specialinstructions: string;
  contacts: LocationContact[];
  ladd?: string;
  ladd2?: string;
  lcity?: string;
  lstate?: string;
  lzip?: string;
  communication?: string;
  lfax?: string;
}

/**
 * Is this a real location that has been retired? The recno guard is the point: recno 0
 * means "no location set" and carries deleted = 'Y' in cms.locations, so a bare
 * `location.deleted` check flags every blank stop on the board.
 */
export const isDeletedLocation = (
  location: { recno?: number | null; deleted?: boolean } | null | undefined,
): boolean => !!location && Number(location.recno) > 0 && !!location.deleted;
